import { useLazyQuery } from '@apollo/client'
import type { SymmetricKey } from '@faceup/crypto'
import { defineMessages, sharedMessages, useIntl } from '@faceup/localization'
import {
  FormItemType,
  isEmail,
  isValidPhoneNumber,
  isWholeNumber,
  LONG_STRING_MAX_LENGTH,
  SHORT_STRING_MAX_LENGTH,
  STRING_MIN_LENGTH,
} from '@faceup/utils'
import type { Dispatch, SetStateAction } from 'react'
import { type FragmentType, graphql } from '../__generated__'
import type { Answer, Answers } from './useManageReportCustomInputs'
import { useReportFiles } from './useReportFiles'

const messages = defineMessages('report.hooks.usePrepareFormItemAnswersForMutation', [
  {
    id: 'invalidPhoneNumber',
    defaultMessage:
      'This phone number is invalid. Please enter the phone number in the correct format.',
  },
  { id: 'invalidWholeNumber', defaultMessage: 'Invalid number.' },
  {
    id: 'invalidEmail',
    defaultMessage: 'This email is invalid. Please enter the email in the correct format.',
  },
  { id: 'max', defaultMessage: 'Maximum length is {max} characters', values: { max: true } },
  { id: 'min', defaultMessage: 'Minimum length is {min} characters', values: { min: true } },
] as const)

const fragments = {
  usePrepareFormItemAnswersForMutationFragments_answer: graphql(`
    fragment usePrepareFormItemAnswersForMutationFragments_answer on FormItemAnswer {
      id
      formItem {
        id
        type
        formItemId
      }
      values
    }
  `),
}

const query = {
  HookCustomFormItemsQuery: graphql(`
    query HookCustomFormItemsQuery(
      $categoryId: CompanyReportCategoryGlobalId
      $reportSourceId: ID!
    ) {
      formItems(categoryId: $categoryId, reportSourceId: $reportSourceId) {
        id
        formItemId
        isRequired(channelId: $reportSourceId)
        type
      }
    }
  `),
}

const omitHardcodedFormItem = (
  formItem: NonNullable<
    FragmentType<
      typeof fragments.usePrepareFormItemAnswersForMutationFragments_answer
    >[' $fragmentRefs']
  >['usePrepareFormItemAnswersForMutationFragments_answerFragment']['formItem']
): boolean =>
  ![
    FormItemType.Category,
    FormItemType.OrganizationalUnit,
    FormItemType.MoreInformation,
    FormItemType.SenderName,
  ].includes(formItem.type)

type ValidateAnswersFn = (
  answers: Answers,
  setAnswers: Dispatch<SetStateAction<Answers>>
) => Promise<boolean>

type GetValuesForMutationFn = (
  answers: Answers,
  key?: SymmetricKey
) => Promise<{ formItemId: string; values: string[] | null }[]>

type PrepareFormFn = (
  reportSourceId: string,
  categoryId: string | null
) => {
  validateAnswers: ValidateAnswersFn
  getValuesForMutation: GetValuesForMutationFn
}

type UsePrepareFormItemAnswersForMutationReturn = {
  prepareForm: PrepareFormFn
}

export const usePrepareFormItemAnswersForMutation =
  (): UsePrepareFormItemAnswersForMutationReturn => {
    const [_getData] = useLazyQuery(query.HookCustomFormItemsQuery)
    const { formatMessage } = useIntl()
    const { encryptFiles } = useReportFiles()

    const prepareForm: PrepareFormFn = (reportSourceId, categoryId) => {
      const getData = async () =>
        await _getData({
          variables: {
            reportSourceId,
            categoryId,
          },
        })

      const validateAnswers: ValidateAnswersFn = async (answers, setAnswers) => {
        const { data } = await getData()
        let ok = true
        data?.formItems.filter(omitHardcodedFormItem).forEach(item => {
          const validate: Record<FormItemType, (value: string) => null | string> = {
            [FormItemType.Scale]: () => null,
            [FormItemType.Select]: () => null,
            [FormItemType.MultiSelect]: () => null,
            [FormItemType.SimpleText]: () => null,
            [FormItemType.Matrix]: () => null,
            [FormItemType.Rating]: () => null,
            [FormItemType.MultilineText]: value =>
              value.length > LONG_STRING_MAX_LENGTH
                ? formatMessage(messages.max, { values: { max: LONG_STRING_MAX_LENGTH } })
                : null,
            [FormItemType.Date]: () => null,
            // here
            [FormItemType.MoreInformation]: () => {
              throw new Error('Implement validation for hardcoded form item')
            },
            [FormItemType.Classroom]: value => {
              if (value.trim().length < STRING_MIN_LENGTH) {
                return formatMessage(messages.min, { values: { min: STRING_MIN_LENGTH } })
              }
              if (value.trim().length > SHORT_STRING_MAX_LENGTH) {
                return formatMessage(messages.max, { values: { max: SHORT_STRING_MAX_LENGTH } })
              }
              return null
            },
            [FormItemType.OrganizationalUnit]: () => {
              throw new Error('Implement validation for hardcoded form item')
            },
            [FormItemType.SenderName]: () => {
              throw new Error('Implement validation for hardcoded form item')
            },
            [FormItemType.Email]: value =>
              isEmail(value) ? null : formatMessage(messages.invalidEmail),
            [FormItemType.PhoneNumber]: value =>
              isValidPhoneNumber(value) ? null : formatMessage(messages.invalidPhoneNumber),
            [FormItemType.Category]: () => {
              throw new Error('Implement validation for hardcoded form item')
            },
            [FormItemType.Number]: value => {
              if (!isWholeNumber(value)) {
                return formatMessage(messages.invalidWholeNumber)
              }
              return null
            },
            [FormItemType.File]: () => null,
          }
          const answerValue = answers[item.formItemId]?.values?.[0]
          if (answerValue) {
            const errorMessage = typeof answerValue === 'string' && validate[item.type](answerValue)
            if (errorMessage) {
              setAnswers(prevState => ({
                ...prevState,
                [item.formItemId]: {
                  type: item.type === FormItemType.File ? 'file' : 'text',
                  values: prevState[item.formItemId]?.values ?? [],
                  error: errorMessage,
                } as Answer,
              }))
              ok = false
            }
          }
          if (item.isRequired && !answerValue) {
            setAnswers(prevState => ({
              ...prevState,
              [item.formItemId]: {
                type: item.type === FormItemType.File ? 'file' : 'text',
                values: prevState[item.formItemId]?.values ?? [],
                error: formatMessage(sharedMessages.fieldRequired),
              } as Answer,
            }))

            ok = false
          }
        })
        return ok
      }

      const getValuesForMutation: GetValuesForMutationFn = async (answers, reportKey) => {
        const { data } = await getData()
        const formItems = data?.formItems

        const fileGuard = (values?: Answer['values']): values is File[] => {
          return Array.isArray(values) && values.every(value => value instanceof File)
        }
        const formItemsWithFiles =
          formItems
            ?.filter(item => item.type === FormItemType.File && fileGuard(answers[item.id]?.values))
            .map(item => ({
              formItemId: item.id,
              files: answers[item.id]?.values as File[],
            })) ?? []
        const encryptedFiles = await encryptFiles(formItemsWithFiles, reportKey)

        return Object.entries(answers)
          .filter(([formItemId]) => {
            const formItem = data?.formItems?.find(formItem => formItem.formItemId === formItemId)
            if (!formItem) {
              return false
            }
            return omitHardcodedFormItem(formItem)
          })
          .map(([formItemId, { values, type }]) => ({
            formItemId,
            ...(type === 'file'
              ? { files: encryptedFiles[formItemId], values: null }
              : { values: values?.map(value => value?.trim()) ?? null }),
          }))
      }

      return {
        validateAnswers,
        getValuesForMutation,
      }
    }

    return {
      prepareForm,
    }
  }
